import i18n from '@/i18n';
import { currentLanguage } from '@/i18n/format';
/**
 * useColumnTranslations — fetches column name translations from the backend
 * and provides a translate(key) function that returns the localized display name.
 *
 * Mirrors the legacy AngularJS pattern:
 *   ErrorData.getColumnLabel(name) → 'global.columns.' + name → angular-translate
 *
 * Now backed by the translations table (category = 'column').
 * Falls back to the raw column key when no translation exists.
 */
import { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';

/** Language storage key — same as auth pattern */

function getCurrentLang(): string {
  return currentLanguage();
}

/**
 * Global translation cache so multiple components share the same data
 * without duplicate requests.
 */
let cachedTranslations: Record<string, Record<string, string>> | null = null;
let fetchPromise: Promise<void> | null = null;
let cacheVersion = 0;

/** Force refetch on next hook mount (e.g., after admin saves) */
export function invalidateColumnTranslations(): void {
  cachedTranslations = null;
  fetchPromise = null;
  cacheVersion++;
}

async function ensureLoaded(): Promise<Record<string, Record<string, string>>> {
  if (cachedTranslations) return cachedTranslations;

  if (!fetchPromise) {
    fetchPromise = apiClient
      .get<Record<string, Record<string, string>>>('/translations/column')
      .then((res) => {
        cachedTranslations = res.data; // { key: { en: "...", tr: "..." } }
      })
      .catch((err) => {
        console.warn('Failed to load column translations:', err);
        cachedTranslations = {};
      });
  }

  await fetchPromise;
  return cachedTranslations!;
}

/**
 * React hook that provides column name translation.
 *
 * Usage:
 *   const { t } = useColumnTranslations();
 *   // In JSX: <th>{t('Population_2017')}</th>
 *   // Returns translated name or falls back to raw key
 */
export function useColumnTranslations() {
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>(
    cachedTranslations || {},
  );
  const [version, setVersion] = useState(cacheVersion);

  useEffect(() => {
    let cancelled = false;
    ensureLoaded().then((data) => {
      if (!cancelled) setTranslations(data);
    });
    // Re-check if cache was invalidated
    if (version !== cacheVersion) {
      setVersion(cacheVersion);
    }
    return () => { cancelled = true; };
  }, [version]);

  /** Translate a single column key */
  const t = useCallback(
    (key: string): string => {
      const lang = getCurrentLang();
      const entry = translations[key];
      if (entry) {
        return entry[lang] || entry['en'] || entry['tr'] || key;
      }
      return key;
    },
    [translations],
  );

  /**
   * Translate an array of column keys.
   * Returns a Map<rawKey, translatedLabel> for efficient lookups.
   */
  const translateAll = useCallback(
    (keys: string[]): Map<string, string> => {
      const lang = getCurrentLang();
      const map = new Map<string, string>();
      for (const key of keys) {
        const entry = translations[key];
        map.set(key, entry ? (entry[lang] || entry['en'] || entry['tr'] || key) : key);
      }
      return map;
    },
    [translations],
  );

  return { t, translateAll, translations };
}

/**
 * Also export a layer name translation hook with the same pattern.
 */
let cachedLayerTranslations: Record<string, Record<string, string>> | null = null;
let layerFetchPromise: Promise<void> | null = null;

export function invalidateLayerTranslations(): void {
  cachedLayerTranslations = null;
  layerFetchPromise = null;
}

async function ensureLayersLoaded(): Promise<Record<string, Record<string, string>>> {
  if (cachedLayerTranslations) return cachedLayerTranslations;

  if (!layerFetchPromise) {
    layerFetchPromise = apiClient
      .get<Record<string, Record<string, string>>>('/translations/layer')
      .then((res) => {
        cachedLayerTranslations = res.data;
      })
      .catch((err) => {
        console.warn('Failed to load layer translations:', err);
        cachedLayerTranslations = {};
      });
  }

  await layerFetchPromise;
  return cachedLayerTranslations!;
}

export function useLayerTranslations() {
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>(
    cachedLayerTranslations || {},
  );

  useEffect(() => {
    let cancelled = false;
    ensureLayersLoaded().then((data) => {
      if (!cancelled) setTranslations(data);
    });
    return () => { cancelled = true; };
  }, []);

  const t = useCallback(
    (layerName: string): string => {
      const lang = getCurrentLang();
      const entry = translations[layerName];
      if (entry) {
        return entry[lang] || entry['en'] || entry['tr'] || layerName;
      }
      // A catchment table named from the suggested groups (CatchmentAge, CatchmentIncome ...) with no translation
      // of its own: the group's name from the app's own words.
      const group = /^Catchment([A-Z][A-Za-z]*)$/.exec(layerName ?? '');
      if (group) {
        return i18n.t(`catchment.group_${group[1]}`, { defaultValue: group[1] });
      }
      return layerName;
    },
    [translations],
  );

  return { t, translations };
}
